import React, { useMemo, useState, useEffect, useRef, useCallback } from "react"
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Stack,
  IconButton,
  Chip,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import AddIcon from "@mui/icons-material/Add"
import StraightenIcon from "@mui/icons-material/Straighten"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import { useTranslation } from "react-i18next"

const BRAND = { red: "#e50019", dark: "#212121" }

const WALL_COLOR = "#c9a97c"
const BASE_COLOR = "#a58660"
const COUNTERTOP_COLOR = "#78909c"
const FULL_BODY_COLOR = "#8d6e63"

// ─── Determine cabinet visual type from category ──────────────
const getCabinetType = (item) => {
  // NEW: Use cabinet_opening field if available (new data model)
  if (item.cabinet_opening) {
    const map = {
      DOOR: "door",
      DRAWER: "drawer",
      LIFT_UP: "flipup",
      OVEN: "oven",
      FRIDGE: "fridge",
      SINK: "sink",
      BLIND: "blind",
    }
    return map[item.cabinet_opening] || "door"
  }

  // Fallback: old category/name parsing
  let cats = []
  try { cats = JSON.parse(item.category || "[]") } catch { cats = [] }
  const cat4 = (cats[3] || "").toLowerCase()
  const name = (item.name || item.description || "").toUpperCase()

  if (cat4 === "drawer" || name.includes("ΣΥΡΤΑΡ")) return "drawer"
  if (cat4 === "lift-up" || name.includes("ΚΟΥΜΠΑΣ")) return "flipup"
  if (cat4 === "oven" || name.includes("ΦΟΥΡΝΟ")) return "oven"
  if (cat4 === "fridge" || name.includes("ΨΥΓΕΙ")) return "fridge"
  if (cat4 === "sink" || name.includes("ΝΕΡΟΧΥΤ")) return "sink"
  return "door" // Hinged or default
}

// ─── Determine item position from new data model ─────────────
const POSITION_MAP = {
  base: "base", wall: "wall", tall: "tall",
  "βασεωσ": "base", "κρεμαστο": "wall", "ολοσωμο": "tall",
  "βάσεωσ": "base", "κρεμαστό": "wall", "ολόσωμο": "tall",
}
const getItemPosition = (item) => {
  // New data model — direct field
  if (item.cabinet_position) return (item.cabinet_position.toLowerCase())
  // Fallback: category array parsing
  let cats = []
  try { cats = JSON.parse(item.category || "[]") } catch { cats = [] }
  const cat2 = (cats[1] || "").toLowerCase()
  return POSITION_MAP[cat2] || cat2
}

// ─── SVG overlays for cabinet openings ────────────────────────
const CabinetOverlay = ({ type, w, h }) => {
  const stroke = "rgba(255,255,255,0.35)"
  const strokeW = 1

  if (type === "drawer") {
    // Horizontal drawer lines
    const count = h > 60 ? 3 : 2
    const lines = []
    for (let i = 1; i <= count; i++) {
      const y = (h / (count + 1)) * i
      lines.push(
        <line key={`d${i}`} x1={4} y1={y} x2={w - 4} y2={y} stroke={stroke} strokeWidth={strokeW} />,
        // Small handle in center
        <line key={`h${i}`} x1={w / 2 - 6} y1={y - 3} x2={w / 2 + 6} y2={y - 3} stroke={stroke} strokeWidth={1.5} strokeLinecap="round" />,
      )
    }
    return <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1, pointerEvents: "none" }}>{lines}</svg>
  }

  if (type === "flipup") {
    // Flip-up: horizontal line near top + up arrow
    return (
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1, pointerEvents: "none" }}>
        <line x1={4} y1={h * 0.25} x2={w - 4} y2={h * 0.25} stroke={stroke} strokeWidth={strokeW} />
        <line x1={w / 2} y1={h * 0.25} x2={w / 2} y2={6} stroke={stroke} strokeWidth={strokeW} />
        <polyline points={`${w / 2 - 4},12 ${w / 2},4 ${w / 2 + 4},12`} fill="none" stroke={stroke} strokeWidth={strokeW} />
      </svg>
    )
  }

  if (type === "oven") {
    // Oven: rectangle inside + handle
    const pad = 5
    return (
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1, pointerEvents: "none" }}>
        <rect x={pad} y={h * 0.15} width={w - pad * 2} height={h * 0.55} rx={2} fill="none" stroke={stroke} strokeWidth={strokeW} />
        <line x1={w * 0.3} y1={h * 0.1} x2={w * 0.7} y2={h * 0.1} stroke={stroke} strokeWidth={1.5} strokeLinecap="round" />
      </svg>
    )
  }

  if (type === "fridge") {
    // Fridge: two sections (top freezer, bottom fridge)
    return (
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1, pointerEvents: "none" }}>
        <line x1={4} y1={h * 0.3} x2={w - 4} y2={h * 0.3} stroke={stroke} strokeWidth={strokeW} />
        <line x1={w - 8} y1={h * 0.12} x2={w - 8} y2={h * 0.25} stroke={stroke} strokeWidth={1.5} strokeLinecap="round" />
        <line x1={w - 8} y1={h * 0.4} x2={w - 8} y2={h * 0.7} stroke={stroke} strokeWidth={1.5} strokeLinecap="round" />
      </svg>
    )
  }

  if (type === "sink") {
    // Sink: bowl shape inside
    return (
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1, pointerEvents: "none" }}>
        <ellipse cx={w / 2} cy={h * 0.5} rx={w * 0.3} ry={h * 0.2} fill="none" stroke={stroke} strokeWidth={strokeW} />
        <circle cx={w / 2} cy={h * 0.35} r={2} fill={stroke} />
      </svg>
    )
  }

  // Default: door(s)
  if (w > 55) {
    // Double door
    return (
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1, pointerEvents: "none" }}>
        <line x1={w / 2} y1={4} x2={w / 2} y2={h - 4} stroke={stroke} strokeWidth={strokeW} />
        <circle cx={w / 2 - 6} cy={h / 2} r={2} fill={stroke} />
        <circle cx={w / 2 + 6} cy={h / 2} r={2} fill={stroke} />
      </svg>
    )
  }
  // Single door
  return (
    <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1, pointerEvents: "none" }}>
      <circle cx={w - 10} cy={h / 2} r={2} fill={stroke} />
    </svg>
  )
}

// ─── Dimension label ──────────────────────────────────────────
const DimLabel = ({ value, sx = {} }) => (
  <Typography sx={{ fontSize: "0.55rem", fontWeight: 700, color: "#475569", lineHeight: 1, ...sx }}>
    {value}
  </Typography>
)

// ─── Draggable cabinet box ────────────────────────────────────
const DraggableCabinetBox = ({
  item, widthPx, heightPx, type, index, rowId,
  isDragging, isDropTarget, dropSide,
  onDragStart, onDragOver, onDragEnd, onDrop, onDelete, onDuplicate,
}) => {
  const hasSwatch = !!item.swatch?.swatchUrl
  const defaultColor = type === "wall" ? WALL_COLOR : type === "full" ? FULL_BODY_COLOR : BASE_COLOR
  const cabinetType = getCabinetType(item)

  return (
    <Box
      draggable
      onDragStart={(e) => onDragStart(e, index, rowId)}
      onDragOver={(e) => onDragOver(e, index, rowId)}
      onDrop={(e) => onDrop(e, index, rowId)}
      onDragEnd={onDragEnd}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "grab",
        opacity: isDragging ? 0.35 : 1,
        transition: "opacity 0.15s",
        position: "relative",
        "&:active": { cursor: "grabbing" },
      }}
    >
      {/* Drop indicators */}
      {isDropTarget && dropSide === "left" && (
        <Box sx={{ position: "absolute", left: -3, top: 12, bottom: 0, width: 3, bgcolor: BRAND.red, borderRadius: 1, zIndex: 10 }} />
      )}
      {isDropTarget && dropSide === "right" && (
        <Box sx={{ position: "absolute", right: -3, top: 12, bottom: 0, width: 3, bgcolor: BRAND.red, borderRadius: 1, zIndex: 10 }} />
      )}

      {/* Width dimension above */}
      <DimLabel value={item._w} sx={{ mb: 0.3 }} />

      {/* The cabinet box */}
      <Box
        sx={{
          width: widthPx,
          height: heightPx,
          borderRadius: 0.75,
          border: isDropTarget ? `2px solid ${BRAND.red}` : "1.5px solid rgba(0,0,0,0.18)",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: hasSwatch ? "transparent" : defaultColor,
          boxShadow: isDragging
            ? "0 4px 12px rgba(0,0,0,0.25)"
            : "inset 0 1px 4px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)",
          transition: "box-shadow 0.15s, border 0.15s",
          "&:hover .delete-btn": { opacity: 1 },
          "&:hover .duplicate-btn": { opacity: 1 },
          "&:hover .drag-handle": { opacity: 1 },
        }}
      >
        {/* Drag handle */}
        <DragIndicatorIcon
          className="drag-handle"
          sx={{
            position: "absolute", top: 1, left: 1, fontSize: 11,
            color: "rgba(255,255,255,0.4)", zIndex: 3, opacity: 0, transition: "opacity 0.15s",
          }}
        />

        {/* Delete button */}
        <Box
          className="delete-btn"
          onClick={(e) => { e.stopPropagation(); onDelete(index, rowId) }}
          sx={{
            position: "absolute", top: 1, right: 1, zIndex: 3,
            width: 14, height: 14, borderRadius: "50%",
            bgcolor: "rgba(229,0,25,0.85)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "0.5rem", fontWeight: 900, lineHeight: 1,
            cursor: "pointer", opacity: 0, transition: "opacity 0.15s",
            "&:hover": { bgcolor: BRAND.red, transform: "scale(1.15)" },
          }}
        >
          ✕
        </Box>

        {/* Duplicate button */}
        <Box
          className="duplicate-btn"
          onClick={(e) => { e.stopPropagation(); onDuplicate(index, rowId) }}
          sx={{
            position: "absolute", bottom: 1, right: 1, zIndex: 3,
            width: 14, height: 14, borderRadius: "50%",
            bgcolor: "rgba(16,185,129,0.85)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", opacity: 0, transition: "opacity 0.15s",
            "&:hover": { bgcolor: "#10b981", transform: "scale(1.15)" },
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 9 }} />
        </Box>

        {/* Swatch texture fill */}
        {hasSwatch && (
          <Box
            component="img"
            src={item.swatch.swatchUrl}
            sx={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover", opacity: 0.85, pointerEvents: "none",
            }}
            onError={(e) => {
              e.target.style.display = "none"
              e.target.parentElement.style.backgroundColor = defaultColor
            }}
          />
        )}

        {/* Door/drawer/opening overlay */}
        <CabinetOverlay type={cabinetType} w={widthPx} h={heightPx} />

        {/* Name label */}
        <Typography
          sx={{
            position: "relative", zIndex: 2,
            fontSize: widthPx > 55 ? "0.5rem" : "0.4rem",
            fontWeight: 700, color: "#fff",
            textShadow: "0 1px 3px rgba(0,0,0,0.75)",
            textAlign: "center", px: 0.3, lineHeight: 1.2,
            overflow: "hidden", textOverflow: "ellipsis",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            maxWidth: "95%", pointerEvents: "none",
          }}
        >
          {item.description || item.name || ""}
        </Typography>

        {/* Depth label */}
        {widthPx > 40 && (
          <Typography
            sx={{
              position: "relative", zIndex: 2,
              fontSize: "0.4rem", color: "rgba(255,255,255,0.7)",
              textShadow: "0 0 2px rgba(0,0,0,0.6)", mt: 0.25, pointerEvents: "none",
            }}
          >
            B:{item._d}
          </Typography>
        )}

        {/* Height dimension */}
        <Typography
          sx={{
            position: "absolute", right: 2, top: "50%", transform: "translateY(-50%)",
            zIndex: 2, fontSize: "0.45rem", fontWeight: 600,
            color: "rgba(255,255,255,0.8)", textShadow: "0 0 3px rgba(0,0,0,0.7)",
            writingMode: "vertical-rl", textOrientation: "mixed", pointerEvents: "none",
          }}
        >
          {item._h}
        </Typography>
      </Box>
    </Box>
  )
}

// ─── Row height annotation (slim line + label) ───────────────
const RowHeightAnnotation = ({ heightMm, heightPx, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", ml: 0.75, height: heightPx, width: 20 }}>
    <svg width="20" height={heightPx} style={{ overflow: "visible" }}>
      {/* Vertical line */}
      <line x1="4" y1="0" x2="4" y2={heightPx} stroke="#94a3b8" strokeWidth="0.75" />
      {/* Top tick */}
      <line x1="0" y1="0" x2="8" y2="0" stroke="#94a3b8" strokeWidth="0.75" />
      {/* Bottom tick */}
      <line x1="0" y1={heightPx} x2="8" y2={heightPx} stroke="#94a3b8" strokeWidth="0.75" />
      {/* Label — rotated text in the middle */}
      <text x="12" y={heightPx / 2} fontSize="7" fill="#94a3b8" fontWeight="600" textAnchor="middle" dominantBaseline="central" transform={`rotate(-90, 12, ${heightPx / 2})`}>
        {heightMm}
      </text>
    </svg>
  </Box>
)

// ─── Expand items by quantity ─────────────────────────────────
const expandItems = (items) =>
  (items || []).flatMap((item) => {
    // Width = productWidth (the varying cabinet width), depth = productDepth (cabinet depth)
    const w = item.customDimensions?.length || parseInt(item.productWidth) || 600
    const h = item.customDimensions?.height || parseInt(item.productHeight) || 720
    const d = parseInt(item.productDepth) || 580
    return Array.from({ length: item.quantity }, (_, i) => ({
      ...item, _w: w, _h: h, _d: d, _key: `${item.cartItemKey}-${i}`,
    }))
  })

const KitchenPreviewModal = ({ open, onClose, groupedCart, cpqState, onUpdateQty, onReorderCart, onAddCabinet }) => {
  const { t } = useTranslation()

  // ─── Compute initial items from cart ──────────────────────
  const initialItems = useMemo(() => {
    // groupedCart maps "KITCHEN" → "Cabinet" now, so this covers both old and new data
    const kouzina = groupedCart.groups?.["Cabinet"] || {}
    return {
      wall: expandItems(kouzina["Wall"]),
      base: expandItems(kouzina["Base"]),
      full: expandItems(kouzina["Tall"]),
    }
  }, [groupedCart])

  // ─── Draggable + deletable state ─────────────────────────
  const [wallItems, setWallItems] = useState([])
  const [baseItems, setBaseItems] = useState([])
  const [leftTallItems, setLeftTallItems] = useState([])
  const [rightTallItems, setRightTallItems] = useState([])

  useEffect(() => {
    setWallItems(initialItems.wall)
    setBaseItems(initialItems.base)
    // Split tall items: first item goes left, rest go right (user can drag between)
    const tall = initialItems.full
    if (tall.length > 0) {
      setLeftTallItems(tall.slice(0, Math.floor(tall.length / 2)))
      setRightTallItems(tall.slice(Math.floor(tall.length / 2)))
    } else {
      setLeftTallItems([])
      setRightTallItems([])
    }
  }, [initialItems])

  // Compatibility: combined tall items for count/reorder
  const fullBodyItems = useMemo(() => [...leftTallItems, ...rightTallItems], [leftTallItems, rightTallItems])

  // ─── Drag state ───────────────────────────────────────────
  const dragRef = useRef({ sourceIndex: null, sourceRow: null })
  const [dragState, setDragState] = useState({ draggingRow: null, draggingIdx: null, overRow: null, overIdx: null, dropSide: null })

  const getRowSetter = useCallback((rowId) => {
    if (rowId === "wall") return setWallItems
    if (rowId === "base") return setBaseItems
    if (rowId === "leftTall") return setLeftTallItems
    if (rowId === "rightTall") return setRightTallItems
    return null
  }, [])

  const handleDragStart = useCallback((e, index, rowId) => {
    dragRef.current = { sourceIndex: index, sourceRow: rowId }
    setDragState((s) => ({ ...s, draggingRow: rowId, draggingIdx: index }))
    e.dataTransfer.effectAllowed = "move"
    const img = new Image()
    img.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
    e.dataTransfer.setDragImage(img, 0, 0)
  }, [])

  const handleDragOver = useCallback((e, index, rowId) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    const srcRow = dragRef.current.sourceRow
    // Allow drag within same row, or between leftTall ↔ rightTall
    const tallRows = ["leftTall", "rightTall"]
    const isTallCross = tallRows.includes(srcRow) && tallRows.includes(rowId)
    if (rowId !== srcRow && !isTallCross) return
    const rect = e.currentTarget.getBoundingClientRect()
    const midX = rect.left + rect.width / 2
    const side = e.clientX < midX ? "left" : "right"
    setDragState((s) => ({ ...s, overRow: rowId, overIdx: index, dropSide: side }))
  }, [])

  const handleDrop = useCallback((e, targetIndex, rowId) => {
    e.preventDefault()
    const { sourceIndex, sourceRow } = dragRef.current
    if (sourceIndex === null) return

    const tallRows = ["leftTall", "rightTall"]
    const isTallCross = tallRows.includes(sourceRow) && tallRows.includes(rowId) && sourceRow !== rowId
    if (sourceRow !== rowId && !isTallCross) return

    const rect = e.currentTarget.getBoundingClientRect()
    const midX = rect.left + rect.width / 2
    const dropAfter = e.clientX >= midX

    if (isTallCross) {
      // Cross-row drag: remove from source, add to target
      const srcSetter = getRowSetter(sourceRow)
      const tgtSetter = getRowSetter(rowId)
      if (!srcSetter || !tgtSetter) return
      const srcItems = sourceRow === "leftTall" ? leftTallItems : rightTallItems
      const movedItem = srcItems[sourceIndex]
      if (!movedItem) return
      srcSetter(prev => prev.filter((_, i) => i !== sourceIndex))
      tgtSetter(prev => {
        const items = [...prev]
        const insertAt = dropAfter ? targetIndex + 1 : targetIndex
        items.splice(Math.max(0, Math.min(insertAt, items.length)), 0, movedItem)
        return items
      })
    } else {
      // Same-row drag: reorder
      const setter = getRowSetter(rowId)
      if (!setter) return
      setter((prev) => {
        const items = [...prev]
        const [moved] = items.splice(sourceIndex, 1)
        let insertAt
        if (sourceIndex < targetIndex) {
          insertAt = dropAfter ? targetIndex : targetIndex - 1
        } else {
          insertAt = dropAfter ? targetIndex + 1 : targetIndex
        }
        insertAt = Math.max(0, Math.min(insertAt, items.length))
        items.splice(insertAt, 0, moved)
        return items
      })
    }

    // Persist reorder to cart
    if (onReorderCart) {
      setTimeout(() => {
        const allRows = [...wallItems, ...leftTallItems, ...baseItems, ...rightTallItems]
        const seen = new Set()
        const orderedKeys = []
        for (const item of allRows) {
          if (item.cartItemKey && !seen.has(item.cartItemKey)) {
            seen.add(item.cartItemKey)
            orderedKeys.push(item.cartItemKey)
          }
        }
        onReorderCart(orderedKeys)
      }, 0)
    }

    dragRef.current = { sourceIndex: null, sourceRow: null }
    setDragState({ draggingRow: null, draggingIdx: null, overRow: null, overIdx: null, dropSide: null })
  }, [getRowSetter, wallItems, baseItems, leftTallItems, rightTallItems, onReorderCart])

  const handleDragEnd = useCallback(() => {
    dragRef.current = { sourceIndex: null, sourceRow: null }
    setDragState({ draggingRow: null, draggingIdx: null, overRow: null, overIdx: null, dropSide: null })
  }, [])

  // ─── Get items array for a row ────────────────────────────
  const getRowItems = useCallback((rowId) => {
    if (rowId === "wall") return wallItems
    if (rowId === "base") return baseItems
    if (rowId === "leftTall") return leftTallItems
    if (rowId === "rightTall") return rightTallItems
    return []
  }, [wallItems, baseItems, leftTallItems, rightTallItems])

  // ─── Delete item from preview + cart ───────────────────────
  const handleDelete = useCallback((index, rowId) => {
    const setter = getRowSetter(rowId)
    if (!setter) return
    const items = getRowItems(rowId)
    const item = items[index]
    setter((prev) => prev.filter((_, i) => i !== index))
    if (item?.cartItemKey && onUpdateQty) {
      onUpdateQty(item.cartItemKey, -1)
    }
  }, [getRowSetter, getRowItems, onUpdateQty])

  // ─── Duplicate item in preview + cart ─────────────────────
  const handleDuplicate = useCallback((index, rowId) => {
    const setter = getRowSetter(rowId)
    if (!setter) return
    const items = getRowItems(rowId)
    const item = items[index]
    setter((prev) => {
      const copy = [...prev]
      const cloned = { ...item, _key: `${item.cartItemKey}-dup-${Date.now()}` }
      copy.splice(index + 1, 0, cloned)
      return copy
    })
    if (item?.cartItemKey && onUpdateQty) {
      onUpdateQty(item.cartItemKey, 1)
    }
  }, [getRowSetter, getRowItems, onUpdateQty])

  // ─── Countertop width ────────────────────────────────────
  const countertopWidthMm = useMemo(() => {
    const pagkosItems = groupedCart.groups?.["Charges"]?.["Worktop"] || []
    const pagkosW = pagkosItems.reduce((sum, i) => sum + (parseInt(i.productWidth) || 0) * i.quantity, 0)
    const baseW = baseItems.reduce((s, i) => s + i._w, 0)
    const wallW = wallItems.reduce((s, i) => s + i._w, 0)
    return pagkosW || Math.max(baseW, wallW) || 2400
  }, [groupedCart, wallItems, baseItems])

  // ─── Calculate scale ──────────────────────────────────────
  const GAP = 3
  const PADDING = 60

  const layout = useMemo(() => {
    const wallW = wallItems.reduce((s, i) => s + i._w, 0) + Math.max(wallItems.length - 1, 0) * (GAP * 6)
    const baseW = baseItems.reduce((s, i) => s + i._w, 0) + Math.max(baseItems.length - 1, 0) * (GAP * 6)
    const leftW = leftTallItems.reduce((s, i) => s + i._w, 0) + Math.max(leftTallItems.length - 1, 0) * (GAP * 6)
    const rightW = rightTallItems.reduce((s, i) => s + i._w, 0) + Math.max(rightTallItems.length - 1, 0) * (GAP * 6)
    const mainW = Math.max(wallW, baseW, countertopWidthMm, 1200)
    const totalW = (leftW > 0 ? leftW + 120 : 0) + mainW + (rightW > 0 ? rightW + 120 : 0)

    const wallH = wallItems.length > 0 ? Math.max(...wallItems.map((i) => i._h)) : 720
    const baseH = baseItems.length > 0 ? Math.max(...baseItems.map((i) => i._h)) : 820
    const leftH = leftTallItems.length > 0 ? Math.max(...leftTallItems.map((i) => i._h)) : 0
    const rightH = rightTallItems.length > 0 ? Math.max(...rightTallItems.map((i) => i._h)) : 0
    const fullH = Math.max(leftH, rightH)
    const gapH = 350
    const countertopH = 40
    const kitchenH = wallH + gapH + countertopH + baseH
    const totalH = Math.max(kitchenH, fullH)

    const AVAILABLE_W = 850 - PADDING
    const AVAILABLE_H = 440
    const scale = Math.min(AVAILABLE_W / totalW, AVAILABLE_H / totalH)

    return { scale, wallH, baseH, leftH, rightH, fullH, gapH, countertopH, kitchenH }
  }, [wallItems, baseItems, leftTallItems, rightTallItems, countertopWidthMm])

  const { scale, wallH, baseH, leftH, rightH, fullH, gapH, countertopH, kitchenH } = layout
  const wallHPx = wallH * scale
  const baseHPx = baseH * scale
  const leftHPx = leftH * scale
  const rightHPx = rightH * scale
  const fullHPx = fullH * scale
  const gapHPx = gapH * scale
  const countertopHPx = Math.max(countertopH * scale, 10)
  const kitchenHPx = kitchenH * scale

  const wallRowPx = wallItems.reduce((s, i) => s + Math.max(i._w * scale, 24), 0) + Math.max(wallItems.length - 1, 0) * GAP
  const baseRowPx = baseItems.reduce((s, i) => s + Math.max(i._w * scale, 24), 0) + Math.max(baseItems.length - 1, 0) * GAP
  const maxRowPx = Math.max(wallRowPx, baseRowPx, countertopWidthMm * scale)

  const isEmpty = wallItems.length === 0 && baseItems.length === 0 && fullBodyItems.length === 0

  // ─── Render a row of draggable cabinets ───────────────────
  const renderRow = (items, rowId, type) => (
    <Box sx={{ display: "flex", gap: `${GAP}px`, justifyContent: "center" }}>
      {items.map((item, idx) => (
        <DraggableCabinetBox
          key={item._key}
          item={item}
          widthPx={Math.max(item._w * scale, 24)}
          heightPx={Math.max(item._h * scale, 30)}
          type={type}
          index={idx}
          rowId={rowId}
          isDragging={dragState.draggingRow === rowId && dragState.draggingIdx === idx}
          isDropTarget={dragState.overRow === rowId && dragState.overIdx === idx && dragState.draggingIdx !== idx}
          dropSide={dragState.overRow === rowId && dragState.overIdx === idx ? dragState.dropSide : null}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragEnd={handleDragEnd}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
        />
      ))}
    </Box>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* Header */}
      <Box
        sx={{
          px: 2.5, py: 1.25, bgcolor: BRAND.dark,
          borderBottom: `2px solid ${BRAND.red}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <StraightenIcon sx={{ fontSize: 20, color: BRAND.red }} />
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#fff" }}>
            {t("Visual Editor")}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {wallItems.length > 0 && (
            <Chip label={`${wallItems.length} ${t("Κρεμαστα")}`} size="small"
              sx={{ fontSize: "0.6rem", height: 22, bgcolor: WALL_COLOR, color: "#fff", fontWeight: 600 }} />
          )}
          {baseItems.length > 0 && (
            <Chip label={`${baseItems.length} ${t("Βασεως")}`} size="small"
              sx={{ fontSize: "0.6rem", height: 22, bgcolor: BASE_COLOR, color: "#fff", fontWeight: 600 }} />
          )}
          {fullBodyItems.length > 0 && (
            <Chip label={`${fullBodyItems.length} ${t("Ολοσωμα")}`} size="small"
              sx={{ fontSize: "0.6rem", height: 22, bgcolor: FULL_BODY_COLOR, color: "#fff", fontWeight: 600 }} />
          )}
          <Chip
            icon={<DragIndicatorIcon sx={{ fontSize: 14, color: "#94a3b8 !important" }} />}
            label={t("Σύρε & Επεξεργασια")}
            size="small"
            sx={{ fontSize: "0.55rem", height: 22, bgcolor: "rgba(255,255,255,0.1)", color: "#94a3b8", fontWeight: 600 }}
          />
          <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ pb: 3, pt: 2 }}>
        {isEmpty ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.9rem", color: "#94a3b8" }}>
              {t("Δεν υπαρχουν κουτια κουζινας στο καλαθι.")}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex", justifyContent: "center", py: 2, px: 2,
              bgcolor: "#fafbfc", borderRadius: 2, border: "1px solid #e2e8f0", minHeight: 300,
            }}
          >
            {/* ── Layout: [Left Tall] [Kitchen Stack] [Right Tall] ── */}
            <Box sx={{ display: "flex", alignItems: "flex-end" }}>

              {/* ══ Left Tall column ══ */}
              {(leftTallItems.length > 0 || onAddCabinet) && (
                <Box sx={{ mr: `${GAP * 3}px`, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Typography sx={{ fontSize: "0.45rem", color: FULL_BODY_COLOR, fontWeight: 700, mb: 0.5, letterSpacing: 0.5, textTransform: "uppercase" }}>
                    {t("Ολοσωμα")}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      borderRight: leftTallItems.length > 0 ? `2px solid ${FULL_BODY_COLOR}50` : "none",
                      pr: leftTallItems.length > 0 ? `${GAP}px` : 0,
                      height: kitchenHPx,
                    }}
                  >
                    {leftTallItems.length > 0 && renderRow(leftTallItems, "leftTall", "full")}
                    {onAddCabinet && (
                      <IconButton
                        onClick={() => onAddCabinet("Tall")}
                        size="small"
                        sx={{
                          ml: 0.5, width: 22, height: 22,
                          border: "1.5px dashed #94a3b8", borderRadius: 0.75,
                          opacity: 0.5, transition: "opacity 0.15s",
                          "&:hover": { opacity: 1, borderColor: BRAND.red, bgcolor: "rgba(229,0,25,0.05)" },
                        }}
                      >
                        <AddIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                      </IconButton>
                    )}
                    {leftTallItems.length > 0 && (
                      <RowHeightAnnotation heightMm={leftH} heightPx={leftHPx} label={t("Ολοσ.")} />
                    )}
                  </Box>
                </Box>
              )}

              {/* ══ Main kitchen column (wall → gap → countertop → base → floor) ══ */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                {/* ── Wall cabinets ── */}
                <Stack direction="row" alignItems="flex-end">
                  {wallItems.length > 0 && renderRow(wallItems, "wall", "wall")}
                  {onAddCabinet && (
                    <IconButton
                      onClick={() => onAddCabinet("Wall")}
                      size="small"
                      sx={{
                        ml: 0.5, width: 22, height: 22,
                        border: "1.5px dashed #94a3b8", borderRadius: 0.75,
                        opacity: 0.5, transition: "opacity 0.15s",
                        "&:hover": { opacity: 1, borderColor: BRAND.red, bgcolor: "rgba(229,0,25,0.05)" },
                      }}
                    >
                      <AddIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                    </IconButton>
                  )}
                  {wallItems.length > 0 && (
                    <RowHeightAnnotation heightMm={wallH} heightPx={wallHPx} label={t("Κρεμ.")} />
                  )}
                </Stack>

                {/* ── Working area gap ── */}
                <Box sx={{ width: maxRowPx, height: gapHPx, display: "flex", alignItems: "center", justifyContent: "center", borderLeft: "1px dashed #e2e8f0", borderRight: "1px dashed #e2e8f0", my: 0.5 }}>
                  <Typography sx={{ fontSize: "0.5rem", color: "#cbd5e1", fontStyle: "italic" }}>{gapH}mm</Typography>
                </Box>

                {/* ── Countertop ── */}
                <Box sx={{ width: maxRowPx, height: countertopHPx, borderRadius: 0.5, bgcolor: cpqState.pagkosColor || COUNTERTOP_COLOR, border: "1.5px solid rgba(0,0,0,0.15)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", mb: 0.25 }}>
                  <Typography sx={{ fontSize: "0.5rem", color: "rgba(255,255,255,0.8)", fontWeight: 700, textShadow: "0 0 2px rgba(0,0,0,0.5)" }}>
                    {t("ΠΑΓΚΟΣ")} — {countertopWidthMm}mm
                  </Typography>
                </Box>

                {/* ── Base cabinets ── */}
                <Stack direction="row" alignItems="flex-start">
                  {baseItems.length > 0 && renderRow(baseItems, "base", "base")}
                  {onAddCabinet && (
                    <IconButton
                      onClick={() => onAddCabinet("Base")}
                      size="small"
                      sx={{
                        ml: 0.5, width: 22, height: 22,
                        border: "1.5px dashed #94a3b8", borderRadius: 0.75,
                        opacity: 0.5, transition: "opacity 0.15s",
                        "&:hover": { opacity: 1, borderColor: BRAND.red, bgcolor: "rgba(229,0,25,0.05)" },
                      }}
                    >
                      <AddIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                    </IconButton>
                  )}
                  {baseItems.length > 0 && (
                    <RowHeightAnnotation heightMm={baseH} heightPx={baseHPx} label={t("Βασ.")} />
                  )}
                </Stack>

                {/* ── Floor line ── */}
                <Box sx={{ width: maxRowPx + 40, mt: 0.75, borderTop: "2px solid #94a3b8", position: "relative" }}>
                  <Typography sx={{ position: "absolute", right: 0, top: 2, fontSize: "0.45rem", color: "#94a3b8", fontStyle: "italic" }}>
                    {t("Δαπεδο")}
                  </Typography>
                </Box>
              </Box>

              {/* ══ Right Tall column ══ */}
              {(rightTallItems.length > 0 || onAddCabinet) && (
                <Box sx={{ ml: `${GAP * 3}px`, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Typography sx={{ fontSize: "0.45rem", color: FULL_BODY_COLOR, fontWeight: 700, mb: 0.5, letterSpacing: 0.5, textTransform: "uppercase" }}>
                    {t("Ολοσωμα")}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      borderLeft: rightTallItems.length > 0 ? `2px solid ${FULL_BODY_COLOR}50` : "none",
                      pl: rightTallItems.length > 0 ? `${GAP}px` : 0,
                      height: kitchenHPx,
                    }}
                  >
                    {rightTallItems.length > 0 && renderRow(rightTallItems, "rightTall", "full")}
                    {onAddCabinet && (
                      <IconButton
                        onClick={() => onAddCabinet("Tall")}
                        size="small"
                        sx={{
                          ml: 0.5, width: 22, height: 22,
                          border: "1.5px dashed #94a3b8", borderRadius: 0.75,
                          opacity: 0.5, transition: "opacity 0.15s",
                          "&:hover": { opacity: 1, borderColor: BRAND.red, bgcolor: "rgba(229,0,25,0.05)" },
                        }}
                      >
                        <AddIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                      </IconButton>
                    )}
                    {rightTallItems.length > 0 && (
                      <RowHeightAnnotation heightMm={rightH} heightPx={rightHPx} label={t("Ολοσ.")} />
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Legend */}
        <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, justifyContent: "center", flexWrap: "wrap", opacity: 0.7 }}>
          <Stack direction="row" alignItems="center" spacing={0.3}>
            <DragIndicatorIcon sx={{ fontSize: 11, color: "#94a3b8" }} />
            <Typography sx={{ fontSize: "0.55rem", color: "#94a3b8" }}>{t("Σύρε")}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.3}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: BRAND.red, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.4rem", color: "#fff", fontWeight: 900 }}>✕</Box>
            <Typography sx={{ fontSize: "0.55rem", color: "#94a3b8" }}>{t("Διαγραφη")}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.3}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#10b981", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ContentCopyIcon sx={{ fontSize: 7, color: "#fff" }} />
            </Box>
            <Typography sx={{ fontSize: "0.55rem", color: "#94a3b8" }}>{t("Αντιγραφη")}</Typography>
          </Stack>
          <Typography sx={{ fontSize: "0.55rem", color: "#cbd5e1" }}>|</Typography>
          <Typography sx={{ fontSize: "0.55rem", color: "#94a3b8" }}>
            {t("Πλατος")} / {t("Υψος")} / B={t("Βαθος")} (mm)
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default KitchenPreviewModal
